import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MdDelete } from "react-icons/md";
import ConfirmationModal from '../../components/modals/ConfirmationModal';
import Toast from '../../components/Toast/Toast';
import { deleteCouponAPI, getCouponsAPI } from '../../utils/api';
import Sidebar from "../Sidebar/Sidebar";
import "./Coupons.css";

const Coupons = () => {
  const navigate = useNavigate();
  const [coupons, setCoupons] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [couponToDelete, setCouponToDelete] = useState(null);

  const PAGE_LIMIT = 10;

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "" }), 3000);
  };

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getCouponsAPI({
        page,
        limit: PAGE_LIMIT,
        search: searchTerm.trim().toLowerCase() || "",
      });

      if (response?.status === 200) {
        const list = response.data;
        const totalPagesFromApi = response?.pagination?.total_pages || 1;
        const currentPageFromApi = response?.pagination?.current_page || page;
        setCoupons(list);
        setTotalPages(totalPagesFromApi);
        if (currentPageFromApi !== page) setPage(currentPageFromApi);
      } else {
        setError(response?.message || 'Failed to fetch coupons');
      }
    } catch (err) {
      console.log('Fetch error:', err);
      setError('Failed to fetch coupons. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoupons();
  }, [page, searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const totalEntries = coupons.length;
  const safePage = Math.min(page, totalPages);
  const startIdx = (safePage - 1) * PAGE_LIMIT;
  const endIdx = startIdx + coupons.length;

  const handleAddNew = () => {
    navigate("/add-coupon");
  };

  const handleDeleteClick = (coupon) => {
    setCouponToDelete(coupon);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      if (!couponToDelete?._id) {
        showToast("Invalid coupon data", "error");
        return;
      }

      const reqData = {
        coupon_id: couponToDelete._id
      };

      const response = await deleteCouponAPI(reqData);

      if (response?.status === 200) {
        setShowDeleteModal(false);
        setCouponToDelete(null);
        showToast(`Coupon deleted successfully!`, "success");
        await fetchCoupons();
      } else {
        showToast(response?.message || 'Failed to delete coupon', "error");
      }
    } catch (err) {
      console.log('Delete error:', err);
      showToast('An error occurred. Please try again.', "error");
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setCouponToDelete(null);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
  };

  const getPageItems = () => {
    const items = [];
    const current = Math.max(1, Math.min(page, totalPages));
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) items.push(i);
      return items;
    }

    items.push(1);
    const showLeftDots = current > 4;
    const showRightDots = current < totalPages - 3;

    if (showLeftDots) items.push('dots-left');

    const start = Math.max(2, current - 1);
    const end = Math.min(totalPages - 1, current + 1);
    for (let i = start; i <= end; i++) items.push(i);

    if (showRightDots) items.push('dots-right');

    items.push(totalPages);
    return items;
  };

  return (
    <>
      <Toast toast={toast} />

      <div className="coupon-layout">
        <Sidebar />
        <div className="coupon-main-content">
          <div className="coupon-header-row">
            <input
              type="text"
              className="search-input"
              placeholder="Search by coupon code"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Search coupons"
            />
            <button
              type="button"
              className="insert-btn"
              onClick={handleAddNew}
              aria-label="Add Coupon"
            >
              <span>Add Coupon</span>
            </button>
          </div>

          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading coupons...</p>
            </div>
          ) : error ? (
            <div className="loading-container">
              <div style={{ color: '#dc2626', fontSize: '1.25rem', marginBottom: '1rem' }}>⚠️</div>
              <p style={{ color: '#dc2626', fontSize: '1.125rem' }}>{error}</p>
            </div>
          ) : totalEntries === 0 ? (
            <div className="empty-state">
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🏷️</div>
              <h3>No Coupons Found</h3>
              <p>Click "Add Coupon" to create the first one.</p>
            </div>
          ) : coupons.length === 0 ? (
            <div className="empty-state">
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔎</div>
              <h3>No results</h3>
              <p>We couldn't find any coupon matching "{searchTerm}".</p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="coupon-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Code</th>
                      <th>Description</th>
                      <th>Discount Type</th>
                      <th>Discount Value</th>
                      <th>Expiry Date</th>
                      <th>Min. Order Amount</th>
                      <th className="actions-col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {coupons.map((coupon, idx) => (
                      <tr key={coupon?._id || `coupon-${idx}`}>
                        <td >{startIdx + idx + 1}</td>
                        <td>{coupon?.code || ''}</td>
                        <td >{coupon?.description || '-'}</td>
                        <td >{coupon?.discount_type || ''}</td>
                        <td >
                          {coupon?.discount_type === 'flat'
                            ? `${coupon?.discount_amount || '0'}`
                            : coupon?.discount_type === 'percentage'
                              ? `${coupon?.discount_percentage || '0'}%`
                              : '-'
                          }
                        </td>
                        <td >{coupon?.expiry_date || '-'}</td>
                        <td >{coupon?.min_order_amount || '-'}</td>
                        <td >
                          <div className="coupon-action-btn-row">
                            <button
                              type="button"
                              className="coupon-action-btn coupon-delete-btn"
                              title="Delete Coupon"
                              onClick={() => handleDeleteClick(coupon)}
                              aria-label="Delete"
                            >
                              <MdDelete />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="table-footer">
                <div className="footer-left">
                  <span>
                    Showing {coupons.length > 0 ? startIdx + 1 : 0} to {endIdx} • Page {page} of {totalPages}
                  </span>
                </div>
                <div className="pagination">
                  <button
                    type="button"
                    disabled={page === 1}
                    onClick={() => handlePageChange(page - 1)}
                    aria-label="Previous page"
                  >
                    Previous
                  </button>
                  {getPageItems().map((item, idx) => (
                    typeof item === 'number' ? (
                      <button
                        type="button"
                        key={`p-${item}`}
                        className={page === item ? 'active' : ''}
                        onClick={() => handlePageChange(item)}
                        aria-current={page === item ? 'page' : undefined}
                      >
                        {item}
                      </button>
                    ) : (
                      <span key={`dots-${idx}`} className="ellipsis" aria-hidden="true">…</span>
                    )
                  ))}
                  <button
                    type="button"
                    disabled={page === totalPages}
                    onClick={() => handlePageChange(page + 1)}
                    aria-label="Next page"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}

          <ConfirmationModal
            visible={showDeleteModal}
            onConfirm={handleDeleteConfirm}
            onCancel={handleDeleteCancel}
            title="Confirm Deletion"
            message={`Are you sure you want to delete the coupon?`}
            confirmText="Delete"
          />
        </div>
      </div>
    </>
  );
};

export default Coupons; 